import { useCallback, useMemo, useState } from "react";
import {
    Avatar,
    Box,
    Button,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    palette,
    Select,
    Stack,
    TextField,
    Tooltip,
} from "@mzc-pdc/ui";
import Typography from "@components_v2/typography/typography";
import theme from "@styles/theme";
import { Trans, useTranslation } from "react-i18next";
import {
    IconChevronDown,
    IconChevronUp,
    IconForward,
    IconOpenInNewSmall,
    ImageFolderSmallOn,
} from "@mzc-cloudplex/icons";
import CategoriesInput from "@features/categories-input/categories-input";
import FolderInput from "@features/folder-input/folder-input";
import ProjectSingleInput from "@features/projects-input/project-single-input";
import { copyAsset } from "@modules/asset";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getProjectRandomColor } from "@utils/get-random-color";
import PreviewAsset from "@routes/asset/preview-asset";
import { setNotification } from "@modules/notification";
import useActionPending from "@hooks/useActionPending";
import { listJobs } from "@modules/bulkJobs";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import menuService from "@services/menuService";
import { useLocation } from "react-router";
import compareVersions, { VERSION_CM_13806_CUSTOM_MENU_MAIL_LINK } from "@cores/version";
import buildMailConfig from "@utils/build-mail-config";
import { EllipsisTypography } from "@components_v2/ellipsis-tooltip";

const DialogAssetDuplicate = ({ isMulti = false, assets, onClickClose, isCustomMenu, isCms, ...props }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();
    const { spaceId, projectId } = menuService.parse(location.pathname);
    const {
        pending: createPending,
        setActionPending: setCreatePending,
        disableActionPending: disableCreatePending,
    } = useActionPending("create");

    const data = assets[0];

    const initializedEditValue = () => {
        if (assets.length > 1) {
            return {
                project: data.project ?? null,
                elements: [],
            };
        } else {
            return {
                name: `Copy to ${data.name}`,
                project: data.project,
                elements: [],
            };
        }
    };

    const [categoryGroupTooltipDisabled, setCategoryGroupTooltipDisabled] = useState(true);
    const [editValue, setEditValue] = useState(initializedEditValue);
    const [isExpanded, setIsExpanded] = useState(false);

    const folderList = useSelector(({ folderTreeView }) => folderTreeView.folderList?.data);
    const stage = useSelector(({ stage }) => stage);

    const getFolderName = (value) => {
        if (!value?.virtualFolder?.id || !folderList) return;
        let currentFolder = folderList[value.virtualFolder.id];

        return currentFolder?.name ?? "";
    };

    const onHandleEditValue = useCallback(
        (key, value) => {
            if (key === "project" && value.id !== projectId) {
                setEditValue({ ...editValue, [key]: value, categories: [], categoryGroup: {} });
            } else {
                setEditValue({ ...editValue, [key]: value });
            }
        },
        [assets, editValue],
    );

    const onCLickAccordionExpand = () => {
        setIsExpanded((prev) => !prev);
    };

    const onHandleChangeCategories = (options, categoryGroup) => {
        let categoryGroupTooltip = categoryGroupTooltipDisabled;

        if (editValue.categoryGroup?.id !== options.categoryGroup?.id) {
            categoryGroupTooltip = false;
        }

        if (options.categoryGroup) {
            setCategoryGroupTooltipDisabled(categoryGroupTooltip);
            setEditValue({
                ...editValue,
                categoryGroup: { id: options.categoryGroup.id, name: options.categoryGroup.label },
                categories: options.categories,
            });
        } else {
            setEditValue({
                ...editValue,
                categories: options,
                categoryGroup: categoryGroup ? { id: categoryGroup.id } : undefined,
            });
        }
    };

    const mailConfig = useMemo(
        () => buildMailConfig({ pathname: location.pathname, search: location.search, isCustomMenu }),
        [location.pathname, location.search, isCustomMenu, isCms],
    );

    const onHandleClickSubmit = async () => {
        try {
            setCreatePending();
            const { project, categories, categoryGroup, virtualFolder, name, elements } = editValue;
            const { id } = data;

            const params = {
                destination: {
                    nameMap: name ? [{ id, name }] : [],
                },
                source: {
                    assets: isMulti ? assets.map(({ id }) => ({ id, elements: [] })) : [{ id, elements }],
                },
            };

            if (project) params.destination.project = { id: project?.id };
            if (categories)
                params.destination.categories = categories?.map((el) => ({
                    id: el.id,
                    groupId: categoryGroup?.id,
                }));

            if (virtualFolder?.driveId && virtualFolder?.folderId)
                params.destination.virtualFolder = { id: virtualFolder?.folderId, driveId: virtualFolder?.driveId };

            if (
                compareVersions(stage.version, VERSION_CM_13806_CUSTOM_MENU_MAIL_LINK) >= 0 &&
                (isCustomMenu || isCms)
            ) {
                params.mailConfig = mailConfig;
            }

            await dispatch(copyAsset(params));
            await dispatch(listJobs());
            await dispatch(
                setNotification({
                    type: "success",
                    contents: t(`common::msg::${"Asset {{item}} duplication completed."}`, {
                        item: data.name,
                    }),
                }),
            );
            onClickClose();
        } catch (e) {
            console.log(e);
        } finally {
            disableCreatePending();
        }
    };

    const getExpandIcon = useMemo(() => {
        if (isExpanded)
            return (
                <Tooltip title={t("common::label::Fold")}>
                    <IconButton
                        variant={"contained"}
                        color="greySecondary"
                        circled
                        size={"small"}
                        onClick={onCLickAccordionExpand}
                    >
                        <IconChevronUp size={16} />
                    </IconButton>
                </Tooltip>
            );
        else
            return (
                <Tooltip title={t("common::label::Expand")}>
                    <IconButton
                        variant={"outlined"}
                        circled
                        size={"small"}
                        color={"greySecondary"}
                        onClick={onCLickAccordionExpand}
                        // sx={{ mt: -0.5 }}
                    >
                        <IconChevronDown size={16} />
                    </IconButton>
                </Tooltip>
            );
    }, [isExpanded]);

    return (
        <Dialog
            variant="basic"
            onClose={() => onClickClose()}
            open={true}
            sx={{ ".MuiDialog-container >.MuiPaper-root": { minWidth: "768px" } }}
        >
            <DialogTitle
                sx={{
                    zIndex: 1,
                }}
            >
                <Grid container>
                    <Typography variant={"h3"}>{t(`common::label::Asset Duplication`, "Asset Duplication")}</Typography>
                </Grid>
            </DialogTitle>
            <DialogContent sx={{ width: "768px", overflowY: "unset" }}>
                {assets && data && (
                    <Grid container flexDirection={"column"} pt={1} pb={3} flexWrap={"nowrap"}>
                        <Grid
                            item
                            sx={{
                                zIndex: 0,
                                paddingBottom: theme.spacing(["VIDEO", "AUDIO"].includes(data.mediaType) ? 0 : 2),
                            }}
                        >
                            {assets.length > 1 ? (
                                <Box
                                    component="fieldset"
                                    sx={{
                                        px: theme.spacing(1.5),
                                        pt: theme.spacing(0.75),
                                        pb: theme.spacing(1.5),
                                        borderRadius: theme.spacing(0.5),
                                        border: `1px solid ${theme.palette.greySecondary[100]}`,
                                    }}
                                >
                                    <legend>
                                        <Typography
                                            pl={theme.spacing(0.5)}
                                            variant="body3_400"
                                            sx={{ px: 0.5 }}
                                            color={theme.palette.greySecondary[600]}
                                        >
                                            {t(`common::label::Duplication target assets`)}
                                        </Typography>
                                    </legend>
                                    <Collapse in={isExpanded} collapsedSize={theme.spacing(4)}>
                                        <Grid container alignItems={isExpanded ? "flex-start" : "baseline"}>
                                            <Grid item xs height={isExpanded ? "auto" : 25}>
                                                {!isExpanded ? (
                                                    <Grid container height={1} alignItems={"center"}>
                                                        <AssetTypeFlag size={"small"} type={data.mediaType} />
                                                        <EllipsisTypography
                                                            variant={"body1_500"}
                                                            tooltipTitle={`"${data.name}"`}
                                                            sx={{
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                                maxWidth: "300px",
                                                            }}
                                                        >
                                                            {`"${data.name}`}
                                                        </EllipsisTypography>
                                                        {`"`}
                                                        <Trans
                                                            i18nKey={`common::msg::and <0>{{count}}</0> more`}
                                                            values={{ count: assets.length - 1 }}
                                                            components={[
                                                                <Typography
                                                                    key="highlight"
                                                                    variant={"body1_500"}
                                                                    color={theme.palette.primary.main}
                                                                />,
                                                            ]}
                                                        />
                                                    </Grid>
                                                ) : (
                                                    <Stack gap={1} maxHeight={224} overflow={"hidden auto"}>
                                                        {assets.map((asset, index) => (
                                                            <Grid
                                                                container
                                                                alignItems={"center"}
                                                                key={`asset-name-${index}`}
                                                            >
                                                                <AssetTypeFlag size={"small"} type={asset.mediaType} />

                                                                <EllipsisTypography
                                                                    variant={"body1_500"}
                                                                    sx={{
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        whiteSpace: "nowrap",
                                                                        maxWidth: "300px", // 원하는 너비 설정
                                                                    }}
                                                                >
                                                                    {asset.name}
                                                                </EllipsisTypography>
                                                                <Stack pr={theme.spacing(0.75)}>{`(${asset.id}`}</Stack>
                                                                <Stack>
                                                                    <Link
                                                                        to={`${menuService.paths.assetDetail(
                                                                            spaceId,
                                                                            projectId,
                                                                            asset.id,
                                                                        )}`}
                                                                        target="_blank"
                                                                    >
                                                                        <IconOpenInNewSmall size={16} />
                                                                    </Link>
                                                                </Stack>
                                                                <Stack>{")"}</Stack>
                                                            </Grid>
                                                        ))}
                                                    </Stack>
                                                )}
                                            </Grid>
                                            <Grid item>{getExpandIcon}</Grid>
                                        </Grid>
                                    </Collapse>
                                </Box>
                            ) : (
                                <TextField
                                    fullWidth
                                    value={data.name}
                                    inputProps={{ readOnly: true }}
                                    sx={{
                                        "&.MuiTextField-root input[readonly]": {
                                            backgroundColor: theme.palette.common.white,
                                        },
                                    }}
                                    label={t(`common::label::Asset to be copied`, "Asset to be copied")}
                                ></TextField>
                            )}
                        </Grid>
                        {!isMulti && (
                            <Grid item pb={3}>
                                <Grid container flexDirection={`column`} gap={1}>
                                    <Grid item>
                                        <Box sx={{ width: 720 }}>
                                            <PreviewAsset
                                                data={data}
                                                mediaType={data.mediaType}
                                                onChange={(elements) => onHandleEditValue("elements", elements)}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                        <Grid
                            container
                            flexDirection={"column"}
                            gap={3}
                            py={2.5}
                            px={2}
                            sx={{
                                borderRadius: "4px",
                                border: `solid 1px ${palette.greySecondary[100]}`,
                            }}
                        >
                            {assets.length === 1 && (
                                <Grid item>
                                    <TextField
                                        fullWidth
                                        label={t(`common::label::Name`)}
                                        value={editValue.name}
                                        onChange={(e) => {
                                            onHandleEditValue("name", e.target.value ?? e.target.defaultValue);
                                        }}
                                        placeholder={t(`common::label::Copy to {{name}}`, "Copy to {{name}}", {
                                            name: data.name,
                                        })}
                                    ></TextField>
                                </Grid>
                            )}
                            <Grid item sx={{ width: "100%" }}>
                                <Stack direction={"column"}>
                                    <Grid container flexDirection={"row"} pb={1.5} gap={7}>
                                        <Grid item xs={5.5}>
                                            <Typography variant={"body3_500"} color={palette.greySecondary[600]}>
                                                {t(`common::label::Current location`)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={5.5}>
                                            <Typography variant={"body3_500"} color={palette.greySecondary[600]}>
                                                {t(`common::label::Where to clone`)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Divider flexItem orientation={"horizontal"} />
                                </Stack>
                            </Grid>

                            {/*프로젝트 선택 영역*/}
                            <Grid item>
                                <Grid container gap={2.5} alignItems={"center"} flexWrap={"nowrap"}>
                                    <Grid item xs={5.5}>
                                        <TextField
                                            fullWidth
                                            value={data.project.name}
                                            label={t(`common::label::Project`)}
                                            InputProps={{
                                                readOnly: true,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Avatar
                                                            sx={{
                                                                width: 20,
                                                                height: 20,
                                                                fontSize: 11,
                                                                fontWeight: `bold`,
                                                                bgcolor: getProjectRandomColor(data.project.id),
                                                            }}
                                                        >
                                                            {`${data.project.name?.charAt(0)}`}
                                                        </Avatar>
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                "&.MuiTextField-root": {
                                                    ".MuiInputBase-readOnly": {
                                                        backgroundColor: `#f8f8f8`,
                                                    },
                                                },
                                            }}
                                        ></TextField>
                                    </Grid>
                                    <Grid item>
                                        <IconForward
                                            size={16}
                                            htmlColor={theme.palette.greySecondary[500]}
                                        ></IconForward>
                                    </Grid>
                                    <Grid item xs={5.5}>
                                        <ProjectSingleInput
                                            data={editValue.project}
                                            defaultValue={editValue.project}
                                            onChange={(v) => onHandleEditValue("project", v ?? data.project)}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/*폴더 위치 선택 영역*/}
                            <Grid item>
                                <Grid container gap={2.5} alignItems={"center"} flexWrap={"nowrap"}>
                                    <Grid item xs={5.5}>
                                        <TextField
                                            fullWidth
                                            value={getFolderName(data)}
                                            label={t(`common::label::Location`)}
                                            InputProps={{
                                                readOnly: true,
                                                startAdornment: (
                                                    <InputAdornment position="start" sx={{ p: 0.25 }}>
                                                        {data.virtualFolder ? (
                                                            <ImageFolderSmallOn width={16} height={16} />
                                                        ) : null}
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                "&.MuiTextField-root": {
                                                    ".MuiInputBase-readOnly": {
                                                        backgroundColor: `#f8f8f8`,
                                                    },
                                                },
                                            }}
                                        ></TextField>
                                    </Grid>
                                    <Grid item>
                                        <IconForward
                                            size={16}
                                            htmlColor={theme.palette.greySecondary[500]}
                                        ></IconForward>
                                    </Grid>
                                    <Grid item xs={5.5}>
                                        <FolderInput
                                            fullWidth
                                            domain={"ASSET"}
                                            projectId={editValue.project?.id}
                                            disablePortal={false}
                                            useDefaultFolder={false}
                                            value={editValue.virtualFolder}
                                            onSubmit={(v) => onHandleEditValue("virtualFolder", v)}
                                            onClose={() => onClickClose()}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/*카테고리 선택 영역*/}
                            <Grid item sx={{ height: "fit-content" }}>
                                <Grid container gap={2.5} alignItems={"center"} flexWrap={"nowrap"}>
                                    <Grid item xs={5.5}>
                                        {!data.categories || data?.categories?.length === 0 ? (
                                            <TextField
                                                fullWidth
                                                disabled
                                                value={"-"}
                                                label={t(`common::label::Category`)}
                                                sx={{
                                                    "&.MuiTextField-root": {
                                                        ".MuiInputBase-readOnly": {
                                                            backgroundColor: `#f8f8f8`,
                                                        },
                                                    },
                                                }}
                                            ></TextField>
                                        ) : (
                                            <FormControl fullWidth>
                                                <InputLabel>{t(`common::label::Category`)}</InputLabel>
                                                <Select
                                                    sx={{
                                                        "&.MuiOutlinedInput-root": {
                                                            backgroundColor: `#f8f8f8`,
                                                        },
                                                        "&:hover .MuiOutlinedInput-notchedOutline": {
                                                            borderColor: theme.palette.greySecondary.light,
                                                        },
                                                    }}
                                                    multiple
                                                    disabled
                                                    value={data.categories?.map((el) => el.name) ?? []}
                                                    IconComponent={null}
                                                    input={
                                                        <OutlinedInput
                                                            disabled
                                                            id="select-multiple-chip"
                                                            label={t(`common::label::Category`, "Category")}
                                                        />
                                                    }
                                                    renderValue={(selected) => (
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                flexWrap: "wrap",
                                                                gap: 0.5,
                                                                WebkitTextFillColor: theme.palette.text.primary,
                                                            }}
                                                        >
                                                            {selected?.map((value) => (
                                                                <Chip
                                                                    variant={"outlined"}
                                                                    size={"small"}
                                                                    key={value}
                                                                    label={value}
                                                                    readOnly
                                                                />
                                                            ))}
                                                        </Box>
                                                    )}
                                                ></Select>
                                            </FormControl>
                                        )}
                                    </Grid>
                                    <Grid item>
                                        <IconForward
                                            size={16}
                                            htmlColor={theme.palette.greySecondary[500]}
                                        ></IconForward>
                                    </Grid>
                                    <Grid item xs={5.5}>
                                        <CategoriesInput
                                            type={"Asset"}
                                            disablePortal={false}
                                            project={editValue.project}
                                            selectedCategoryGroup={{
                                                id: editValue.categoryGroup?.id,
                                                label: editValue.categoryGroup?.name,
                                            }}
                                            tooltipDisabled={categoryGroupTooltipDisabled}
                                            placeholder={t(`common::label::${"Select category"}`)}
                                            selectedValues={editValue.categories ?? []}
                                            onChange={(options, categoryGroup) => {
                                                onHandleChangeCategories(options, categoryGroup);
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <Divider flexItem orientation={"horizontal"} />
            <DialogActions>
                <Button color="secondary" onClick={onClickClose} variant="text">
                    {t(`common::label::Cancel`, "Cancel")}
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={onHandleClickSubmit}
                    loading={createPending}
                    disabled={createPending}
                >
                    {t(`common::label::Duplicate`, "Duplicate")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DialogAssetDuplicate;
